import { A, type RouteSectionProps, useNavigate } from "@solidjs/router";
import { type Component, createMemo, For, Show, type VoidComponent } from "solid-js";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { Breadcrumb } from "../../../components/breadcrumb/Breadcrumb";
import { useCustomerState } from "../../../state/customer";
import { useTranslation } from "../../../state/i18n";
import UserService from "../../../services/UserService";
import UserId from "../../../state/UserId";
import { resolveFullName, resolveUserOrInviteName } from "../../../utils/userUtil";
import Text from "../../../components/text/Text";
import Pill from "../../../components/pill/Pill";
import { IconButton, TextAndIconButton, TextButton } from "../../../components/button/Button";
import EditUserNameModal from "./EditUserNameModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import DeleteUserModal from "./DeleteUserModal";
import { isEmpty, isNil } from "lodash-es";
import { AddUserToGroupsModal } from "./AddUserToGroupsModal";
import GlobalTabs from "../../../components/tabs/GlobalTabs";
import ApiService from "../../../services/ApiService";
import type { GroupedRoles } from "../../../utils/roleUtil";
import {
  getAccountRole,
  getAllRoles,
  getGroupCustomerPermissions,
  getRoleDescription,
  getRoleName,
  groupRoles,
  orderRolesByName,
  toPermissionInfos,
  toRoleByIdMap,
} from "../../../utils/roleUtil";
import type { Role } from "../../../state/role";
import type { Group } from "../../../state/group";
import type { Folder } from "../../../types/Folder";
import type { AccountOwner } from "../../../types/AccountOwner";
import { Icon } from "../../../components/icon/Icon";
import { getGroupsForUser, resolveName as resolveGroupName } from "../../../utils/groupUtil";
import TransferOwnershipModal from "./TransferOwnershipModal";
import { CustomerRoleSelect } from "./UserManagement/CustomerRoleSelect";
import { PermissionList } from "./UserManagement/PermissionList";
import type { Grant } from "../../../state/grant";
import GenericFailureModal from "../../GenericFailureModal";
import type { MoreAppError } from "../../../MoreAppError";
import type { User } from "../../../state/user";
import type { Invite } from "../../../state/invite.ts";
import InviteId from "../../../state/InviteId.ts";
import { IconTile } from "../../../components/icon/IconTile";
import { FolderAccordion } from "./UserManagement/FolderAccordion";
import type RoleId from "../../../state/RoleId";
import type FolderId from "../../../state/FolderId";
import type FormId from "../../../state/FormId";
import type { PermissionType } from "../../../state/Permission.ts";
import { HIDDEN_PERMISSION_TYPES } from "../../../state/Permission.ts";
import RemoveUserFromGroupModal from "./RemoveUserFromGroupModal";
import EmptyState from "../../../components/emptyState/EmptyState";
import { condition, narrow } from "../../../utils/solidUtils.ts";
import PillSingleSelectTrigger from "../../../components/menu/triggers/PillSingleSelectTrigger.tsx";
import { MemberAvatar } from "../../../components/avatar/Avatar.tsx";
import InlineMessage from "../../../components/inlineMessage/InlineMessage.tsx";
import { Translation } from "../../../utils/Translation.tsx";
import { keyFromSelector } from "i18next";
import type { RoleIconAppearanceType } from "./UserManagement/RolePermissionDisplay.tsx";
import { RoleIconAppearance } from "./UserManagement/RolePermissionDisplay.tsx";

const UserPage: Component<RouteSectionProps<never>> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const customer = useCustomerState();
  const { open } = useModalManager();

  const accountOwner = UserService.getAccountOwner(customer.customerId);
  const userId = createMemo(() => UserId.from(props.params.userId!));
  const inviteId = createMemo(() => InviteId.from(props.params.userId!));
  const userState = createMemo(() => UserService.getUserById(customer.customerId, userId(), true));
  const inviteState = createMemo(() => UserService.getInvite(customer.customerId, inviteId(), true));
  const rolesState = UserService.getRoles(customer.customerId);
  const groupsState = UserService.getGroups(customer.customerId);
  const foldersState = ApiService.getFolders();

  const roles = createMemo(() => {
    if (isNil(rolesState.data)) {
      return;
    }

    return groupRoles(orderRolesByName(rolesState.data, t));
  });

  type ResolvedState = (
    | {
        isInvited: true;
        user: Invite;
      }
    | {
        isInvited: false;
        user: User;
      }
  ) & {
    accountOwner: AccountOwner;
    accountRole?: Role;
    roles: Role[];
    groupedRoles: GroupedRoles;
    groups: Group[];
    folders: Folder[];
    grants: Grant[];
  };

  const resolvedState = createMemo<ResolvedState | undefined>(() => {
    const user = userState().data;
    const invite = isNil(user) ? inviteState().data : undefined;

    const resolvedUser = user ?? invite;
    const groupedRoles = roles();

    if (
      isNil(accountOwner.data) ||
      isNil(resolvedUser) ||
      isNil(groupedRoles) ||
      isNil(groupsState.data) ||
      isNil(foldersState.data) ||
      isNil(rolesState.data)
    ) {
      return undefined;
    }

    const accountRole = getAccountRole(resolvedUser.grants, groupedRoles);

    const commonFields = {
      accountOwner: accountOwner.data,
      accountRole,
      groupedRoles,
      groups: groupsState.data,
      folders: foldersState.data,
      grants: resolvedUser.grants,
      roles: rolesState.data,
    };

    if (!isNil(user)) {
      return {
        user,
        isInvited: false,
        ...commonFields,
      };
    }

    if (!isNil(invite)) {
      return {
        user: invite,
        isInvited: true,
        ...commonFields,
      };
    }
  });

  return (
    <Show when={resolvedState()}>
      {(resolvedState) => (
        <div class="flex h-full min-h-0 flex-col gap-6 text-header-xl text-primary">
          <Breadcrumb
            currentLabel={(() => resolveUserOrInviteName(resolvedState().isInvited, resolvedState().user))()}
            crumbs={[{ label: "Users", href: `/customers/${customer.customerId}/users` }]}
          />
          <div class="flex min-w-0 items-center gap-6">
            <div class="flex min-w-0 grow items-center gap-3">
              <MemberAvatar member={resolvedState().user} size="xl" />
              <div class="flex min-w-0 flex-col">
                <div class="flex items-center gap-3">
                  <Show
                    when={narrow(resolvedState, (resolved) => !resolved.isInvited)}
                    fallback={<Pill variant="neutral" label={t(($) => $.USER_PAGE_INVITED)} />}
                  >
                    {(resolved) => (
                      <Text
                        text={resolveFullName(resolved().user)}
                        as="h2"
                        class="shrink text-header-xl text-primary"
                        truncate
                      />
                    )}
                  </Show>
                  <Show
                    when={condition(
                      resolvedState(),
                      (resolved) => !resolved.isInvited && resolved.user.externallyManaged,
                    )}
                  >
                    <Pill variant="informative" label={t(($) => $.USERS_PAGE_SSO_MANAGED)} />
                  </Show>
                </div>
                <Text
                  text={resolvedState().user.emailAddress}
                  as="h2"
                  class="text-medium-regular text-secondary"
                  truncate
                />
              </div>
            </div>
            <div class="flex gap-2">
              <TextAndIconButton
                text={t(($) => $.USER_PAGE_ADD_TO_GROUP_LABEL)}
                icon="plus"
                variant="light"
                size="large"
                onClick={() =>
                  open({
                    content: () => <AddUserToGroupsModal user={resolvedState().user} />,
                  })
                }
              />
              <Show
                when={condition(resolvedState(), (resolved) => resolved.isInvited || !resolved.user.externallyManaged)}
              >
                <Show when={narrow(resolvedState, (resolved) => !resolved.isInvited)}>
                  {(resolved) => (
                    <IconButton
                      icon="pencil"
                      aria-label={t(($) => $.USER_PAGE_EDIT_USER_ARIA)}
                      variant="secondary"
                      size="large"
                      onClick={() => {
                        open({
                          content: () => <EditUserNameModal user={resolved().user} />,
                        });
                      }}
                    />
                  )}
                </Show>
                <IconButton
                  icon="trash"
                  aria-label={t(($) => $.USER_PAGE_DELETE_USER_ARIA)}
                  variant="secondary"
                  size="large"
                  onClick={() =>
                    open({
                      content: () => (
                        <DeleteUserModal
                          user={resolvedState().user}
                          onSuccess={() => navigate(`/customers/${customer.customerId}/users`)}
                        />
                      ),
                    })
                  }
                />
              </Show>
            </div>
          </div>
          <div class="min-h-0 grow">
            <GlobalTabs
              items={[
                {
                  label: () => t(($) => $.GROUPS_ROLE_PERMISSION_TAB_LABEL),
                  key: "role_permissions",
                  Slot: () => (
                    <div class="flex flex-col gap-10">
                      <UserPermissions {...resolvedState()} />
                    </div>
                  ),
                },
                {
                  label: () => t(($) => $.USER_PAGE_MEMBERSHIP_TAB_TITLE),
                  key: "members",
                  Slot: () => <GroupMemberships {...resolvedState()} />,
                },
              ]}
              initialTab="role_permissions"
            />
          </div>
        </div>
      )}
    </Show>
  );
};

type UserOrInvite = { user: User; isInvited: false } | { user: Invite; isInvited: true };

type UserPermissionProps = UserOrInvite & {
  accountOwner: AccountOwner;
  accountRole?: Role;
  roles: Role[];
  groupedRoles: GroupedRoles;
  groups: Group[];
  folders: Folder[];
  grants: Grant[];
};

const UserPermissions: VoidComponent<UserPermissionProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();

  const customer = useCustomerState();
  const updateGrantMutation = UserService.updateGrant(customer);
  const openErrorModal = (err: MoreAppError) => open({ content: () => <GenericFailureModal error={err} /> });

  return (
    <Show when={props.accountRole}>
      {(accountRole) => {
        const isAccountOwner = createMemo<boolean>(
          () => !props.isInvited && props.accountOwner.id.equals(props.user.id),
        );

        const permissionsFromAccountRole = createMemo(() =>
          toPermissionInfos(
            [...new Set(accountRole().permissions)]
              // Hide internal "SYSTEM" and legacy permissions in the interface
              .filter((permission) => !HIDDEN_PERMISSION_TYPES.has(permission))
              .map((permission): [PermissionType, Group[]] => [permission, []]),
            t,
          ),
        );

        const accountRoleById = createMemo(() =>
          toRoleByIdMap([...props.groupedRoles.accountCustomRoles, ...props.groupedRoles.accountSystemRoles]),
        );

        const permissionsFromGroups = createMemo(() => {
          const accountRolePermissions = new Set(accountRole().permissions);

          const groupsByPermission = new Map<PermissionType, Group[]>();

          getGroupsForUser(props.user, props.groups).forEach((group) => {
            const groupPermissions = getGroupCustomerPermissions(group, accountRoleById()).filter(
              (permission) => !accountRolePermissions.has(permission),
            );

            new Set(groupPermissions).forEach((permission) => {
              groupsByPermission.set(permission, [...(groupsByPermission.get(permission) ?? []), group]);
            });
          });

          return toPermissionInfos(groupsByPermission.entries(), t);
        });

        const hasManageForms = createMemo(() => {
          if (accountRole().permissions.includes("MANAGE_FORMS")) {
            return true;
          }
          return getGroupsForUser(props.user, props.groups).some((group) =>
            getGroupCustomerPermissions(group, accountRoleById()).includes("MANAGE_FORMS"),
          );
        });

        const iconAppearance = createMemo<RoleIconAppearanceType>(() =>
          isAccountOwner()
            ? { icon: "crown", color: "icon-accent-yellow", bgColor: "bg-accent-subtle-yellow" }
            : RoleIconAppearance(accountRole()),
        );

        return (
          <div class="my-6">
            <div class="flex flex-col overflow-hidden rounded-sm border border-subtle">
              <div class="flex items-center gap-3 p-6">
                <IconTile size="lg" {...iconAppearance()} />
                <div class="flex grow flex-col items-start gap-1">
                  <Show
                    when={!isAccountOwner()}
                    fallback={
                      <PillSingleSelectTrigger
                        value={{
                          variant: "text",
                          label: getRoleName(accountRole(), t),
                          value: accountRole().id,
                        }}
                        disabled
                      />
                    }
                  >
                    <CustomerRoleSelect
                      roles={props.groupedRoles}
                      value={accountRole()}
                      onSelect={(roleId) =>
                        updateGrantMutation.mutate(
                          {
                            userOrInviteId: props.user.id,
                            isInvited: props.isInvited,
                            resourceType: "CUSTOMER",
                            resourceId: customer.id,
                            roleId,
                            currentRoleId: accountRole().id,
                          },
                          { onError: openErrorModal },
                        )
                      }
                    />
                  </Show>
                  <Text
                    as="p"
                    class="text-base-regular text-secondary"
                    text={getRoleDescription(accountRole(), t) ?? ""}
                  />
                </div>
                <Show when={isAccountOwner()}>
                  <TextButton
                    class="shrink-0"
                    variant="secondary"
                    text={t(($) => $.USERS_PAGE_TRANSFER_OWNERSHIP)}
                    onClick={() => open({ content: () => <TransferOwnershipModal /> })}
                  />
                </Show>
              </div>

              <Show when={!isEmpty(permissionsFromAccountRole()) || !isEmpty(permissionsFromGroups())}>
                <div class="flex flex-col gap-6 border-t border-subtle bg-surface-extra-subtle p-6">
                  <Show when={!isEmpty(permissionsFromAccountRole())}>
                    <div>
                      <Text
                        as="h2"
                        text={t(($) => $.USER_PAGE_ACCOUNT_PERMISSIONS_TITLE)}
                        class="text-medium-semibold text-primary"
                      />
                      <Text
                        as="p"
                        text={t(($) => $.USER_PAGE_ACCOUNT_PERMISSIONS_DESCRIPTION)}
                        class="text-base-regular text-secondary"
                      />
                    </div>
                    <PermissionList permissions={permissionsFromAccountRole()} />
                  </Show>

                  <Show when={!isEmpty(permissionsFromGroups())}>
                    <Show when={!isEmpty(permissionsFromAccountRole())}>
                      <div class="border-t border-subtle" />
                    </Show>
                    <Text
                      text={t(($) => $.USER_PAGE_INHERITED_PERMISSIONS_TITLE)}
                      class="text-base-medium text-secondary"
                    />
                    <PermissionList permissions={permissionsFromGroups()} />
                  </Show>
                </div>
              </Show>
            </div>

            <Show when={!isEmpty(props.folders)}>
              <div class="mt-10 flex flex-col gap-4">
                <div>
                  <Text
                    as="h2"
                    text={t(($) => $.USER_PAGE_FORM_FOLDER_PERMISSIONS_TITLE)}
                    class="text-medium-semibold text-primary"
                  />
                  <Text
                    as="p"
                    text={t(($) => $.USER_PAGE_FORM_FOLDER_PERMISSIONS_DESCRIPTION)}
                    class="text-base-regular text-secondary"
                  />
                </div>

                <Show when={hasManageForms()}>
                  <InlineMessage variant="informative">
                    <Translation
                      key={keyFromSelector(($) => $.USER_PAGE_MANAGE_ALL_FORMS_PERMISSIONS)}
                      components={{
                        b: (props) => <span class="text-base-semibold">{props.children}</span>,
                      }}
                    />
                  </InlineMessage>
                </Show>

                <FolderAccordion
                  folders={props.folders}
                  roles={props.roles}
                  groupedRoles={props.groupedRoles}
                  grants={props.grants}
                  groups={props.groups}
                  user={props.user}
                  selectFolderRole={
                    hasManageForms()
                      ? undefined
                      : (roleId: RoleId | null, folderId: FolderId, currentRoleId: RoleId | null) =>
                          updateGrantMutation.mutate(
                            {
                              userOrInviteId: props.user.id,
                              isInvited: props.isInvited,
                              resourceType: "FOLDER",
                              resourceId: folderId,
                              roleId,
                              currentRoleId,
                            },
                            { onError: openErrorModal },
                          )
                  }
                  selectFormRole={(roleId: RoleId | null, formId: FormId, currentRoleId: RoleId | null) =>
                    updateGrantMutation.mutate(
                      {
                        userOrInviteId: props.user.id,
                        isInvited: props.isInvited,
                        resourceType: "FORM",
                        resourceId: formId,
                        roleId,
                        currentRoleId,
                      },
                      { onError: openErrorModal },
                    )
                  }
                />
              </div>
            </Show>
          </div>
        );
      }}
    </Show>
  );
};

export type ResourceAccess = {
  role: Role;
  groups: Group[];
  direct: boolean;
};

const GroupMemberships: VoidComponent<UserPermissionProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();

  const customer = useCustomerState();
  const groupsForUser = createMemo(() => getGroupsForUser(props.user, props.groups));

  const roleById = createMemo(() => toRoleByIdMap(getAllRoles(props.groupedRoles)));
  const groupRole = (group: Group): Role | undefined => {
    const grant = group.grants.find((g) => g.resourceType === "CUSTOMER");
    return isNil(grant) ? undefined : roleById().get(grant.roleId.toString());
  };
  const inheritedPermissionCount = (group: Group) => new Set(getGroupCustomerPermissions(group, roleById())).size;

  return (
    <Show
      when={!isEmpty(groupsForUser())}
      fallback={
        <EmptyState
          title={t(($) => $.USER_PAGE_GROUP_MEMBERSHIPS_EMPTY_TITLE)}
          description={t(($) => $.USER_PAGE_GROUP_MEMBERSHIPS_EMPTY_DESCRIPTION)}
          illustration={{ primaryIcon: "userGroup" }}
        />
      }
    >
      <div
        role="table"
        style={{
          "--column-template": "calc(46% - 2.625rem) 1fr 1fr 2.5rem",
        }}
        class="mt-6 flex min-h-0 flex-1 flex-col"
      >
        <div role="rowgroup">
          <div
            role="row"
            class="grid grid-cols-template gap-3 border-b border-subtle text-base-semibold text-secondary"
          >
            <div role="columnheader" class="px-3 py-1.5">
              {t(($) => $.USER_PAGE_NAME_COLUMN_TITLE)}
            </div>
            <div role="columnheader" class="py-1.5">
              {t(($) => $.USER_PAGE_INHERTIS_COLUMN_TITLE)}
            </div>
            <div role="columnheader" class="py-1.5">
              {t(($) => $.USER_PAGE_ROLE_COLUMN_TITLE)}
            </div>
            <div role="columnheader" class="w-10" />
          </div>
        </div>
        <div role="rowgroup" class="min-h-0 flex-1 overflow-y-auto pb-6">
          <For each={groupsForUser()}>
            {(group) => (
              <div class="relative">
                <A
                  href={`/customers/${customer.customerId}/groups/${group.id.toString()}`}
                  aria-label={resolveGroupName(group)}
                  class="absolute inset-0 rounded-sm -outline-offset-1 hover:bg-surface-hover focus-visible:outline-default"
                />
                <div
                  role="row"
                  class="pointer-events-none relative grid grid-cols-template items-center gap-3 text-base-regular text-secondary"
                >
                  <div role="cell" class="flex min-w-0 items-center gap-2.5 px-3 py-4">
                    <Text
                      text={resolveGroupName(group)}
                      as="span"
                      class="shrink text-base-medium text-primary"
                      truncate
                    />
                    <Show when={group.externallyManaged}>
                      <Pill variant="informative" label={t(($) => $.USERS_PAGE_SSO_MANAGED)} />
                    </Show>
                  </div>

                  <div role="cell" class="flex min-w-0 items-center gap-2 py-4">
                    <Icon name="hierarchy" size="sm" type="solid" />
                    <Text
                      text={t(($) => $.USER_PAGE_INHERITED_PERMISSIONS_COUNT, {
                        count: inheritedPermissionCount(group),
                      })}
                      as="span"
                      truncate
                    />
                  </div>
                  <div role="cell" class="flex min-w-0 items-center gap-2.5 py-4">
                    <Show when={groupRole(group)} fallback={t(($) => $.USER_PAGE_NONE)}>
                      {(role) => <Pill variant="neutral" label={getRoleName(role(), t)} class="self-start" />}
                    </Show>
                  </div>

                  <div role="cell">
                    <Show when={!group.externallyManaged}>
                      <IconButton
                        icon="trash"
                        aria-label={t(($) => $.GROUPS_PAGE_REMOVE_USER)}
                        variant="tertiary"
                        class="pointer-events-auto"
                        onClick={() =>
                          open({
                            content: () => (
                              <RemoveUserFromGroupModal
                                customerId={customer.customerId}
                                group={group}
                                user={props.user}
                              />
                            ),
                          })
                        }
                      />
                    </Show>
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </Show>
  );
};

const ProtectedUserPage = withPagePermissionCheck(UserPage, PermissionService.canManagePermissions);

export default ProtectedUserPage;
